/**
 * 邮箱配置模态框组件
 * 在头像菜单中直接打开，提供邮箱配置的管理功能
 * 优化后的简洁版本
 */

import React, { useState, useEffect } from "react";
import { Modal, Button, Space, Tag, message, Typography, Empty, Spin, Card, Divider, Alert, Table } from "antd";
import { PlusOutlined, EditOutlined, CheckCircleOutlined, ExclamationCircleOutlined, ExperimentOutlined, MailOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { userEmailConfigService } from "../../../api/enhancedApi";
import { getPresetSenderAccounts } from "../../../api/mailApi";
import { useAuth } from "../../../hooks/useAuth";
import EmailConfigForm from "./EmailConfigForm";

const { Text } = Typography;

const EmailConfigModal = ({ visible, onClose }) => {
    const { user } = useAuth(); // 获取当前用户信息
    const [emailConfig, setEmailConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [testing, setTesting] = useState(false);
    const [presetAccounts, setPresetAccounts] = useState([]); // 预设发件账号列表
    const [presetLoading, setPresetLoading] = useState(false);

    // 加载邮箱配置
    const loadConfig = async () => {
        if (!visible) return;

        setLoading(true);
        try {
            const response = await userEmailConfigService.getUserEmailConfigs({
                page: 1,
                page_size: 1,
            });

            if (response.data && response.data.items.length > 0) {
                setEmailConfig(response.data.items[0]);
            } else {
                setEmailConfig(null);
            }
        } catch (error) {
            console.error("加载邮箱配置失败:", error);
            message.error("加载邮箱配置失败");
        } finally {
            setLoading(false);
        }
    };

    // 当模态框打开时加载配置
    useEffect(() => {
        if (visible) {
            loadConfig();
        }
    }, [visible]);

    // 加载预设发件账号（仅 Admin 可见）
    const loadPresetAccounts = async () => {
        if (!user?.is_superuser) return;
        
        setPresetLoading(true);
        try {
            const response = await getPresetSenderAccounts();
            if (response.success) {
                setPresetAccounts(response.data || []);
            }
        } catch (error) {
            console.error("加载预设发件账号失败:", error);
        } finally {
            setPresetLoading(false);
        }
    };

    // 当 user 和 visible 改变时，加载预设账号
    useEffect(() => {
        if (visible && user) {
            loadPresetAccounts();
        }
    }, [visible, user]);

    // 新增邮箱配置
    const handleAdd = () => {
        setFormVisible(true);
    };

    // 编辑邮箱配置
    const handleEdit = () => {
        setFormVisible(true);
    };

    // 测试邮箱配置
    const handleTest = async () => {
        if (!emailConfig) return;

        setTesting(true);
        try {
            const response = await userEmailConfigService.testEmailConfig(emailConfig.id);
            if (response.data?.success) {
                message.success(response.data.message || "邮箱测试成功");
                loadConfig();
            } else {
                message.error(response.data?.message || "邮箱测试失败");
            }
        } catch (error) {
            console.error("邮箱测试失败:", error);
            message.error("邮箱测试失败");
        } finally {
            setTesting(false);
        }
    };

    // 表单提交成功回调
    const handleFormSuccess = () => {
        setFormVisible(false);
        loadConfig();
    };

    // 关闭模态框
    const handleClose = () => {
        setFormVisible(false);
        onClose();
    };

    // 渲染邮箱配置内容
    const renderContent = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>
                        <Text type="secondary">加载中...</Text>
                    </div>
                </div>
            );
        }

        if (!emailConfig) {
            return (
                <>
                    <Alert message="此邮箱配置仅用于发送邮件，不用于接收邮件。" type="info" showIcon style={{ marginBottom: 24 }} />
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <div>
                                <Text>您还没有配置邮箱</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    配置后可使用个人邮箱发送系统邮件
                                </Text>
                            </div>
                        }
                        style={{ padding: "40px 0" }}
                    >
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                            立即配置
                        </Button>
                    </Empty>
                </>
            );
        }

        return (
            <div>
                <Alert message="此邮箱配置仅用于系统发送邮件，不用于接收邮件。" type="info" showIcon style={{ marginBottom: 24 }} />
                {/* 邮箱信息卡片 */}
                <Card
                    size="small"
                    style={{ marginBottom: 16 }}
                    title={
                        <Space>
                            <MailOutlined />
                            <span>邮箱信息</span>
                            {emailConfig.is_verified ? <CheckCircleOutlined style={{ color: "#52c41a" }} /> : <ExclamationCircleOutlined style={{ color: "#faad14" }} />}
                        </Space>
                    }
                    extra={
                        <Space>
                            <Button size="small" icon={<ExperimentOutlined />} loading={testing} onClick={handleTest}>
                                测试
                            </Button>
                            <Button size="small" type="primary" icon={<EditOutlined />} onClick={handleEdit}>
                                编辑
                            </Button>
                        </Space>
                    }
                >
                    <div style={{ lineHeight: "24px" }}>
                        <div style={{ marginBottom: 8 }}>
                            <Text strong>配置名称：</Text>
                            <Text>{emailConfig.config_name}</Text>
                        </div>

                        <div style={{ marginBottom: 8 }}>
                            <Text strong>邮箱地址：</Text>
                            <Text code>{emailConfig.email_address}</Text>
                        </div>

                        {emailConfig.display_name && (
                            <div style={{ marginBottom: 8 }}>
                                <Text strong>显示名称：</Text>
                                <Text>{emailConfig.display_name}</Text>
                            </div>
                        )}

                        <div style={{ marginBottom: 8 }}>
                            <Text strong>状态：</Text>
                            <Space>
                                <Tag color={emailConfig.is_active ? "success" : "default"}>{emailConfig.is_active ? "启用" : "禁用"}</Tag>
                                <Tag color="gold">默认邮箱</Tag>
                            </Space>
                        </div>
                    </div>
                </Card>

                {/* SMTP配置信息 */}
                <Card size="small" title="SMTP配置" style={{ marginBottom: 16 }}>
                    <div style={{ lineHeight: "24px" }}>
                        <div style={{ marginBottom: 8 }}>
                            <Text strong>服务器：</Text>
                            <Text code>
                                {emailConfig.smtp_server}:{emailConfig.smtp_port}
                            </Text>
                        </div>

                        <div style={{ marginBottom: 8 }}>
                            <Text strong>加密方式：</Text>
                            {emailConfig.smtp_use_ssl ? <Tag color="green">SSL</Tag> : emailConfig.smtp_use_tls ? <Tag color="blue">TLS</Tag> : <Tag>无</Tag>}
                        </div>

                        {emailConfig.last_test_at && (
                            <div style={{ marginBottom: 8 }}>
                                <Text strong>最后测试：</Text>
                                <Text type="secondary">{new Date(emailConfig.last_test_at).toLocaleString()}</Text>
                            </div>
                        )}

                        {emailConfig.last_test_result && (
                            <div style={{ marginBottom: 8 }}>
                                <Text strong>测试结果：</Text>
                                <div style={{ marginTop: 4 }}>
                                    <Text
                                        type={emailConfig.is_verified ? "success" : "danger"}
                                        style={{
                                            fontSize: 12,
                                            padding: "4px 8px",
                                            backgroundColor: emailConfig.is_verified ? "#f6ffed" : "#fff2f0",
                                            border: `1px solid ${emailConfig.is_verified ? "#b7eb8f" : "#ffccc7"}`,
                                            borderRadius: 4,
                                            display: "inline-block",
                                        }}
                                    >
                                        {emailConfig.last_test_result}
                                    </Text>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>

                {/* 使用说明 */}
                <div
                    style={{
                        padding: 12,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 6,
                        fontSize: 12,
                    }}
                >
                    <Text type="secondary">💡 配置完成后，系统将使用您的邮箱发送各类邮件。 请确保邮箱支持SMTP服务并已开启相关权限。</Text>
                </div>

                {/* 系统预设发件账号面板（仅 Admin 可见） */}
                {user?.is_superuser && (
                    <Card
                        size="small"
                        style={{ marginTop: 16 }}
                        title={
                            <Space>
                                <span>系统预设发件账号</span>
                                <Tag color="orange">Admin Only</Tag>
                            </Space>
                        }
                        loading={presetLoading}
                    >
                        {presetAccounts.length > 0 ? (
                            <Table
                                dataSource={presetAccounts}
                                rowKey="key"
                                pagination={false}
                                size="small"
                                columns={[
                                    {
                                        title: "账号标识",
                                        dataIndex: "key",
                                        key: "key",
                                    },
                                    {
                                        title: "邮箱地址",
                                        dataIndex: "email",
                                        key: "email",
                                        render: (text) => <Text code>{text}</Text>,
                                    },
                                    {
                                        title: "显示名称",
                                        dataIndex: "display_name",
                                        key: "display_name",
                                    },
                                    {
                                        title: "描述",
                                        dataIndex: "description",
                                        key: "description",
                                        render: (text) => text || <Text type="secondary">-</Text>,
                                    },
                                ]}
                            />
                        ) : (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <div>
                                        <Text>未配置预设发件账号</Text>
                                        <br />
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            请在 .env 文件中配置 PRESET_SENDER_ACCOUNTS，并重启后端服务
                                        </Text>
                                    </div>
                                }
                            />
                        )}
                    </Card>
                )}
            </div>
        );
    };

    return (
        <>
            <Modal title="我的邮箱配置" open={visible} onCancel={handleClose} footer={null} width={520} destroyOnHidden>
                {renderContent()}
            </Modal>

            {/* 邮箱配置表单模态框 */}
            <Modal title={emailConfig ? "编辑邮箱配置" : "添加邮箱配置"} open={formVisible} onCancel={() => setFormVisible(false)} footer={null} width={600} destroyOnHidden>
                <EmailConfigForm initialValues={emailConfig} onSuccess={handleFormSuccess} onCancel={() => setFormVisible(false)} />
            </Modal>
        </>
    );
};

export default EmailConfigModal;
